import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CellAlert, WSClientMsgCellAlertsMsg } from '@liveline/ll_node_common/lib/models/websocket';
import { RootState } from '../state/features/reducers';
import { addCellAlerts, clearCellAlerts } from '../state/features/websocketReducer';
import { WarningNotification, FailedNotification } from '../components/shared/notification/NotificationComponent';
import useCanViewCellTuning from './useCanViewCellTuning';
import { buildCellAlertsTopic, sanitizeTopicFilters } from '../utils/kafkaTopics';
import { formatAlertMessage, resolveAlertSensor } from '../utils/alertSensor';
import { takeNewCellAlerts } from '../utils/cellAlertsReplay';
import useAlertTagCatalogue from './useAlertTagCatalogue';

const HEARTBEAT_MS = 30000;
const RECONNECT_MS = 10000;

/**
 * Audiences the current user may see. operator+engineer for everyone;
 * controls/internal require the LivelineControls screen (v1 simplification —
 * both are Liveline-staff audiences today).
 */
export const useVisibleAudiences = (): string[] => {
    const canControls = useCanViewCellTuning();
    return useMemo(
        () => (canControls ? ['operator', 'engineer', 'controls', 'internal'] : ['operator', 'engineer']),
        [canControls],
    );
};

/** Membership test — audience is an OPEN set; unknown values never break filtering. */
export const isAlertVisible = (alert: CellAlert, visibleAudiences: string[]): boolean => {
    if (!Array.isArray(alert.audience) || alert.audience.length === 0) return true; // defensive
    return alert.audience.some((a) => visibleAudiences.includes(a));
};

/** Liveline-staff audiences — hard-gated by role, never exposed as opt-in chips. */
export const RESTRICTED_AUDIENCES = ['controls', 'internal'];

/**
 * Hard role gate: block an alert only when EVERY audience on it is a
 * restricted one the user lacks. Unknown audiences (open set, e.g. a future
 * "quality") pass the gate — they surface as default-off chips in the drawer
 * instead of vanishing entirely.
 */
export const isAlertRoleBlocked = (alert: CellAlert, visibleAudiences: string[]): boolean => {
    if (!Array.isArray(alert.audience) || alert.audience.length === 0) return false; // defensive
    return !alert.audience.some(
        (a) => !RESTRICTED_AUDIENCES.includes(a) || visibleAudiences.includes(a),
    );
};

/**
 * Maintains a DEDICATED websocket connection for the CellAlerts live feed
 * (LT-3052), independent of the shared per-facility signal-data connection so
 * page mount/unmount can never tear the alert feed down. Subscribes to
 * CellAlerts_{cellId} for every cell of the selected facility (both the
 * OnPrem.-mirrored and cloud-native topic names), dispatches incoming alerts
 * into the websocket redux slice, and fires toasts for live warning/critical
 * alerts the user is allowed to see.
 */
export const useCellAlerts = () => {
    const dispatch = useDispatch();
    const socketURL = useSelector((s: RootState) => s.auth?.user?.socketURL);
    const token = useSelector((s: RootState) => s.auth?.user?.accessToken);
    const facility = useSelector((s: RootState) => s.userSelection?.facility);
    const cells = useSelector((s: RootState) => s.cellList?.data);
    const visibleAudiences = useVisibleAudiences();

    const facilityId: number = facility?.id ?? 0;
    const isCloudOnly: boolean = Boolean((facility as any)?.isCloudOnly);
    const cellIds = useMemo(() => (cells ?? []).map((c) => c.id).filter(Boolean), [cells]);
    // id → display name ("Line 5"), so the footer can name a cell the way the
    // rest of the console does instead of echoing the raw facility_line_id.
    const cellNames = useMemo(() => {
        const map: Record<number, string> = {};
        (cells ?? []).forEach((c) => {
            if (c?.id && c?.name) map[c.id] = c.name;
        });
        return map;
    }, [cells]);

    const alerts = useSelector((s: RootState) => s.websocket.cellAlerts[facilityId]) ?? [];
    // LT-3320 — per-cell sensor names for tag-scoped alerts. Read through a ref by the toast
    // below, which runs inside the socket's onmessage and would otherwise see the first render's map.
    const tagCatalogue = useAlertTagCatalogue(facilityId, alerts);
    const tagCatalogueRef = useRef(tagCatalogue);
    tagCatalogueRef.current = tagCatalogue;

    // The CURRENT run's socket, for the filter re-send below. Everything else about a
    // connection lives inside the effect run that opened it (LT-3368).
    const socketRef = useRef<WebSocket | null>(null);
    const visibleAudiencesRef = useRef(visibleAudiences);
    visibleAudiencesRef.current = visibleAudiences;
    const cellIdsRef = useRef(cellIds);
    // Token lives in a ref so the recurring Redux accessToken refresh (the
    // LT-2973 client-churn pattern) can NOT bounce the socket — reconnects
    // read the freshest token at open time instead.
    const tokenRef = useRef(token);
    tokenRef.current = token;

    // Topics are built via buildCellAlertsTopic and sanitized before the send (LT-3186).
    // `cellIds` is already `.filter(Boolean)`-ed, so nothing should ever be dropped here —
    // this hook owns its own socket and so bypasses the websocketService backstop, and the
    // guarantee that no "CellAlerts_undefined" can reach the wire has to hold here too.
    const sendAlertFilters = (socket: WebSocket, ids: number[], cloudOnly: boolean) => {
        if (socket.readyState !== WebSocket.OPEN || ids.length === 0) return;
        const prefixes = cloudOnly ? [''] : ['OnPrem.', ''];
        const filters = sanitizeTopicFilters(
            ids
                .flatMap((id) => prefixes.map((p) => buildCellAlertsTopic(p, id)))
                .filter((topic): topic is string => topic !== null)
                .map((kafkaTopic) => ({ kafkaTopic, kafkaPartition: 0 })),
            'cellAlerts',
        );
        if (!filters.length) return;
        try {
            socket.send(JSON.stringify(filters));
        } catch (e) {
            console.error('cellAlerts: failed to send filters', e);
        }
    };

    // Connection lifecycle — per facility
    useEffect(() => {
        if (!facilityId || !socketURL) return;
        // Per RUN, not refs shared across runs (LT-3368). The browser fires onclose
        // asynchronously, so a superseded socket's onclose lands after the next run has
        // already started; with a shared "closed" flag the next run had reset it, and the
        // old socket scheduled a reconnect and lived on — one more per facility switch.
        let closed = false;
        let socket: WebSocket | null = null;
        let heartbeat: ReturnType<typeof setInterval> | null = null;
        let reconnect: ReturnType<typeof setTimeout> | null = null;
        // Alert occurrences already folded for this facility, so a reconnect's snapshot
        // replay only adds what was missed. Same lifetime as the stored list.
        const seen = new Set<string>();

        const buildUrl = (): string => {
            let url = socketURL;
            if (process.env.REACT_APP_STAGE !== 'prod-internal') {
                url += `/${facilityId}`;
                if (tokenRef.current) url += `?token=${tokenRef.current}`;
            }
            return url;
        };

        const open = () => {
            if (closed) return;
            let ws: WebSocket;
            try {
                ws = new WebSocket(buildUrl());
            } catch (e) {
                console.error('cellAlerts: websocket open failed', e);
                reconnect = setTimeout(open, RECONNECT_MS);
                return;
            }
            socket = ws;
            socketRef.current = ws;

            ws.onopen = () => {
                sendAlertFilters(ws, cellIdsRef.current, isCloudOnly);
                if (heartbeat) clearInterval(heartbeat);
                heartbeat = setInterval(() => {
                    try {
                        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'hb' }));
                    } catch {
                        /* heartbeat failures surface via onclose */
                    }
                }, HEARTBEAT_MS);
            };

            ws.onmessage = (event: MessageEvent) => {
                if (closed) return; // a closing socket can still deliver; its facility is gone
                try {
                    const msg = JSON.parse(event.data) as WSClientMsgCellAlertsMsg;
                    if (msg?.type !== 'cellAlerts' || !Array.isArray(msg.alerts)) return;
                    // A snapshot is ONE topic's replay, merged like any push — never a
                    // list to replace the facility's with (LT-3368).
                    const fresh = takeNewCellAlerts(msg.alerts, seen);
                    if (fresh.length === 0) return;
                    dispatch(addCellAlerts({ facilityId, alerts: fresh }));
                    if (!msg.snapshot) {
                        fresh
                            .filter((a) => a.state !== 'cleared' && isAlertVisible(a, visibleAudiencesRef.current))
                            .forEach((a) => {
                                // Named by the sensor's alias once its cell's catalogue is in; the
                                // raw message until then (a cell's first-ever alert can beat it).
                                const text = formatAlertMessage(
                                    a,
                                    resolveAlertSensor(a, tagCatalogueRef.current.get(a.facility_line_id)),
                                );
                                if (a.severity === 'critical') FailedNotification(a.code, text);
                                else if (a.severity === 'warning') WarningNotification(a.code, text);
                            });
                    }
                } catch {
                    /* non-JSON or non-alert traffic on this socket is ignored */
                }
            };

            ws.onclose = () => {
                if (heartbeat) {
                    clearInterval(heartbeat);
                    heartbeat = null;
                }
                if (!closed) {
                    reconnect = setTimeout(open, RECONNECT_MS);
                }
            };
            ws.onerror = () => {
                /* onclose follows and schedules the reconnect */
            };
        };

        open();

        return () => {
            closed = true;
            if (heartbeat) clearInterval(heartbeat);
            if (reconnect) clearTimeout(reconnect);
            try {
                socket?.close();
            } catch {
                /* already closed */
            }
            if (socketRef.current === socket) socketRef.current = null;
            dispatch(clearCellAlerts(facilityId));
        };
        // token intentionally NOT a dep — see tokenRef above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, socketURL, isCloudOnly, dispatch]);

    // Re-send filters when the facility's cell list changes while connected
    useEffect(() => {
        cellIdsRef.current = cellIds;
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            sendAlertFilters(socket, cellIds, isCloudOnly);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellIds.join(','), isCloudOnly]);

    return { alerts, facilityId, visibleAudiences, cellNames, tagCatalogue };
};

export default useCellAlerts;
